/* eslint-disable prefer-destructuring */
import _ from 'underscore';
import $ from 'jquery';
import Promise from 'bluebird';
import { withUiBlock } from '@bingads-webui/uiblocker';
import {
  getClientIdForGMC,
  clientIdFetchErrorText,
  googleApiInitializationFailedErrorText,
  idpiframeInitializationFailedErrorText,
  popupBlockedByBrowserErrorText,
  popupClosedByUserErrorText,
  accessDeniedErrorText,
  authorizationErrorText,
  signInErrorCodes,
} from '@bingads-webui-campaign-react/gmc-to-mmc-mapping-grid-shared';
import { GMCLoginRepository } from './gmc-login-repository';

const loadGoogleDependency = google => new Promise((resolve, reject) => {
  if (!google) {
    window.require(['ext/google/gsi'], () => {
      if (window.google) {
        resolve();
      } else {
        reject(signInErrorCodes.GapiInitFailed);
      }
    }, () => {
      reject(signInErrorCodes.GapiInitFailed);
    });
  } else {
    resolve();
  }
});

// This class provides Google Merchant Center login method.
// Firstly, get client ID from MMC MT.
// Then, use the google to trigger Google sign in page.
export class GMCLoginWithClientIdRepository {
  constructor({
    google,
    i18n,
    logger,
    aid,
    cid,
    uid,
    storeId,
    smallToken,
    oAuthToken,
    devToken,
  }) {
    // Note: the google needs to be loaded first.
    // For load, use loadPageWebpack with hasGoogleApi param in feature-bootstrap.
    this.google = google;
    this.i18n = i18n;
    this.logger = logger;
    this.aid = aid;
    this.cid = cid;
    this.uid = uid;
    this.storeId = storeId;
    this.smallToken = smallToken;
    this.oAuthToken = oAuthToken;
    this.devToken = devToken;
  }

  signInWithGetClientId = ({
    clientId = null, setClientId = null, getClientIdOnSuccess, getClientIdOnError, onSuccess, onFail, useConsentPrompt, GSILocal,
  }) => {
    const unblock = withUiBlock($('.app-shell-content'), this.i18n.getString(_TL_('Loading')));

    const signInWithClientId = () => {
      new Promise((resolve, reject) => {
        this.gmcLoginRepository.googleSignIn({
          onSuccess: resolve,
          onFail: reject,
          useConsentPrompt,
        });
      }).then(response => onSuccess(response))
        .catch((error) => {
          const parsedError = this.parseSignInError({ error });
          onFail(parsedError);
        })
        .finally(() => {
          unblock();
        });
    };

    // The GMC login repository will be undefined if this is the first time the user signed-in,
    // or we were unable to get the client ID
    if (!_.isUndefined(this.gmcLoginRepository)) {
      signInWithClientId();
    } else {
      this.getClientIdOnSuccess = (response) => {
        const {
          ClientId,
        } = response;
        return new Promise((resolve) => {
          loadGoogleDependency(this.google)
            .then(() => {
              if (!this.google) {
                this.google = window.google;
              }

              this.gmcLoginRepository = new GMCLoginRepository({
                clientId: ClientId,
                google: this.google,
                GSILocal,
              });

              getClientIdOnSuccess(response);

              resolve();
            })
            .catch((error) => {
              const parsedError = this.parseSignInError({ error: { authErrors: error } });
              onFail(parsedError);

              unblock();
            });
        });
      };
      this.getClientIdOnError = () => {
        const parsedError = this.parseGetClientIdError();
        getClientIdOnError(parsedError);

        unblock();
      };

      // Make another call to get the client ID. This supports the scenario where the user
      // clicks the sign-in button after seeing the error text
      this.getClientId({
        additionalOnSuccess: signInWithClientId, clientId, setClientId, defaultOnSuccess: this.getClientIdOnSuccess, defaultOnError: this.getClientIdOnError,
      });
    }
  };

  // We need to call MMC to get the client_id used to sign-in to GMC.
  // MMC will use this client_id to make calls to get information about the GMC stores.
  // MMC will later lookup client_id by customerId when using the refresh token
  // to create new MMC stores.
  getClientId = ({
    additionalOnSuccess = null, clientId = null, setClientId = null, defaultOnSuccess = _.noop, defaultOnError = _.noop,
  }) => {
    // Replace below code with the following comment to test GMC Login.
    // const clientId = '590632461859-fik532l8jcm0e6lam3juujssrq4t5vp3.apps.googleusercontent.com';
    // this.gmcLoginRepository = new GMCLoginRepository({
    //   clientId,
    // });

    // TODO: note that multi-account import will not have access to aid
    const aid = this.aid;

    // TODO: Think about Multi-CID agency scenario for client IDs and how it impacts
    // SetMappings and GetMappings regarding getting the client IDs by CID in MMC's DB design
    // TODO: will need to refactor this to support multiple CIDs
    // when implementing multi-account import and its agency scenario
    const cid = this.cid;

    const uid = this.uid;

    const storeId = this.storeId;

    if (!clientId) {
      getClientIdForGMC({
        aid,
        cid,
        uid,
        storeId,
        onSuccess: (response) => {
          defaultOnSuccess(response)
            .then(() => {
              if (setClientId) {
                setClientId(response.ClientId);
              }

              /* istanbul ignore else */
              if (!_.isNull(additionalOnSuccess)) {
                additionalOnSuccess();
              }
            });
        },
        onError: defaultOnError,
        logger: this.logger,
        smallToken: this.smallToken,
        oAuthToken: this.oAuthToken,
        devToken: this.devToken,
      });
    } else {
      // get client id from cache and call additional on success
      defaultOnSuccess({ ClientId: clientId })
        .then(() => {
          /* istanbul ignore else */
          if (!_.isNull(additionalOnSuccess)) {
            additionalOnSuccess();
          }
        });
    }
  };

  parseGetClientIdError = () => ({
    errorCode: 'get_client_id_failed',
    loginErrorText: clientIdFetchErrorText(this.i18n),
  });

  parseSignInError = ({ error }) => {
    let loginErrorText = null;
    let errorCode = error.authErrors;

    if (error.authErrors === signInErrorCodes.GapiInitFailed) {
      loginErrorText = googleApiInitializationFailedErrorText(this.i18n);
    } else if (error.authErrors === signInErrorCodes.InitFailed) {
      loginErrorText = idpiframeInitializationFailedErrorText(this.i18n);
    } else if (error.authErrors === signInErrorCodes.PopupBlockedGSI) {
      loginErrorText = popupBlockedByBrowserErrorText(this.i18n);
    } else if (error.authErrors === signInErrorCodes.PopupClosedGSI) {
      loginErrorText = popupClosedByUserErrorText(this.i18n);
    } else if (error.authErrors === signInErrorCodes.AccessDenied) {
      loginErrorText = accessDeniedErrorText(this.i18n);
    } else {
      errorCode = signInErrorCodes.AuthorizationError;
      loginErrorText = authorizationErrorText(this.i18n);
    }
    return {
      errorCode,
      loginErrorText,
    };
  };
}
